import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual, isNil, isNull, toNumber, toString } from 'lodash';
import { FormikProps } from 'formik';
import { Button, Modal, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import { AsyncThunk } from '@reduxjs/toolkit';

import {
  crateClientFromDraft,
  createDraftClient,
  createNewClient,
  updateClient,
  updateDraftClient,
} from 'redux/reducers/clientSlice';
import {
  CreateCustomerRequest,
  CreateCustomerResponse,
  DurationTypeEnum,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import { getTabValidationIcon } from 'utils/tabUtils';
import GeneralInfo, {
  GeneralInfoFormValues,
  isGeneralInfoFormValidForDraft,
  PrimaryContactFormValues,
} from './GeneralInfo';
import AdditionalInfo, {
  AdditionalInfoFormValues,
  isAdditionalInfoFormValidForDraft,
} from './AdditionalInfo';
import ServiceAgreement, {
  isServiceAgreementValidForDraft,
  ServiceAgreementFormValues,
} from './ServiceAgreement';
import BillingInfo, {
  BillingInfoFormValues,
  isBillingInfoFormValidForDraft,
} from './BillingInfo';
import useTabs from 'hooks/useTabs';
import { ClientCreationEnum } from './enum';
import { TabValidationStatuses } from 'enums';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import { getModalSubmitName, getModalTitle } from 'utils/clientUtils';
import {
  getAllCities,
  getAllCountries,
  getAllCountryCities,
  getAllStates,
} from 'redux/reducers/regionSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { FileValue } from 'components/wrappers/BaseFileUpload';

enum TabKeys {
  GeneralInfo = 0,
  BillingInfo = 1,
  AdditionalInfo = 2,
  ServiceAgreement = 3,
}

export interface ClientInitialValues {
  generalInfo: GeneralInfoFormValues;
  billingInfo: BillingInfoFormValues;
  serviceAgreement: ServiceAgreementFormValues;
  additionalInfo: AdditionalInfoFormValues;
}

type GeneralInfoFormikProps = FormikProps<GeneralInfoFormValues>;
type BillingInfoFormikProps = FormikProps<BillingInfoFormValues>;
type ServiceAgreementFormikProps = FormikProps<ServiceAgreementFormValues>;
type AdditionalInfoFormikProps = FormikProps<AdditionalInfoFormValues>;

type Props = {
  onClose: () => void;
  onSubmit?: () => void;
  creationType?: ClientCreationEnum;
  initialValues?: ClientInitialValues;
  customerId?: string;
  isDraftAvailable?: boolean;
  onSetClientValues?: (
    values: CreateCustomerRequest,
    customerId: string,
  ) => void;
};

const getInitialTabs = (isEdit: boolean = false) => [
  {
    title: 'General information',
    status: TabValidationStatuses.Wait,
    isDraftValid: isEdit ? true : false,
    draftValidationFunc: isGeneralInfoFormValidForDraft,
  },
  {
    title: 'Billing information',
    status: TabValidationStatuses.Wait,
    isDraftValid: true,
    draftValidationFunc: isBillingInfoFormValidForDraft,
  },
  {
    title: 'Additional information',
    status: TabValidationStatuses.Success,
    isDraftValid: true,
    draftValidationFunc: isAdditionalInfoFormValidForDraft,
  },
  {
    title: 'Agreement details',
    status: TabValidationStatuses.Wait,
    isDraftValid: true,
    draftValidationFunc: isServiceAgreementValidForDraft,
  },
];

export default function ClientModal(props: Props) {
  const {
    onClose,
    onSubmit,
    creationType = ClientCreationEnum.Create,
    initialValues,
    isDraftAvailable = true,
    customerId,
    onSetClientValues,
  } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const [activeTab, setActiveTab] = useState(TabKeys.GeneralInfo);
  const [tabs, onFormChange] = useTabs(getInitialTabs(!!initialValues));
  const [isLoading, setIsLoading] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const isDraftValid = tabs.every((tab) => tab.isDraftValid);

  useModalCloseOnNavigate(onClose);

  const generalInfoFormRef: any = useRef();
  const billingInfoFormRef: any = useRef();
  const serviceAgreementFormRef: any = useRef();
  const additionalInfoFormRef: any = useRef();

  useEffect(() => {
    const loadModalData = () => {
      dispatch(getAllStates());
      dispatch(getAllCities());
      dispatch(getAllCountries());
      dispatch(getAllCountryCities());
    };

    loadModalData();
  }, [dispatch]);

  const handleTabChange = (activeTab: string) =>
    setActiveTab(toNumber(activeTab));

  const handleNextStep = () => setActiveTab((step) => step + 1);
  const handlePrevStep = () => setActiveTab((step) => step - 1);

  const isFormDirty = () => {
    return [
      generalInfoFormRef.current?.dirty,
      billingInfoFormRef.current?.dirty,
      serviceAgreementFormRef.current?.dirty,
      additionalInfoFormRef.current?.dirty,
    ].some(Boolean);
  };

  const isFormValid = () => {
    return [
      generalInfoFormRef.current?.isValid,
      billingInfoFormRef.current?.isValid,
      serviceAgreementFormRef.current?.isValid,
      additionalInfoFormRef.current?.isValid,
    ].every(Boolean);
  };

  const isFormSubmitting = () => {
    const generalInfoForm: GeneralInfoFormikProps = generalInfoFormRef.current;
    const billingInfoForm: BillingInfoFormikProps = billingInfoFormRef.current;
    const additionalInfoForm: AdditionalInfoFormikProps =
      additionalInfoFormRef.current;
    const serviceAgreementForm: ServiceAgreementFormikProps =
      serviceAgreementFormRef.current;

    return [
      generalInfoForm.isSubmitting,
      billingInfoForm.isSubmitting,
      additionalInfoForm.isSubmitting,
      serviceAgreementForm.isSubmitting,
    ].every(Boolean);
  };

  const resetFormSubmitting = () => {
    [
      generalInfoFormRef?.current,
      billingInfoFormRef?.current,
      additionalInfoFormRef?.current,
      serviceAgreementFormRef?.current,
    ].forEach((form) => form?.setSubmitting(false));
  };

  const handleModalCancel = () => {
    if (isFormSubmitting() || isDraftSaving) return;

    if (isFormDirty()) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const billingLocation = useMemo(() => {
    const generalInfoForm: GeneralInfoFormValues =
      generalInfoFormRef.current?.values;
    return {
      country: generalInfoForm?.country,
      state: generalInfoForm?.state,
      internationalStateName: generalInfoForm?.internationalStateName,
      city: generalInfoForm?.city,
      internationalCityName: generalInfoForm?.internationalCityName,
      address: generalInfoForm?.mainAddress,
      zip: generalInfoForm?.zip,
    };
    // eslint-disable-next-line
  }, [generalInfoFormRef.current?.values]);

  const handleSubmit = async () => {
    if (isDraftSaving) return;

    const generalInfoForm: GeneralInfoFormikProps = generalInfoFormRef.current;
    const billingInfoForm: BillingInfoFormikProps = billingInfoFormRef.current;
    const additionalInfoForm: AdditionalInfoFormikProps =
      additionalInfoFormRef.current;
    const serviceAgreementForm: ServiceAgreementFormikProps =
      serviceAgreementFormRef.current;

    await Promise.all([
      generalInfoForm.submitForm(),
      billingInfoForm.submitForm(),
      serviceAgreementForm.submitForm(),
      additionalInfoForm.submitForm(),
    ]);

    if (!isFormValid()) return;

    // Prevent form submission if there were no changes.
    // https://mybasepay.atlassian.net/browse/MBO-2778
    if (creationType === ClientCreationEnum.Edit) {
      const filterPrimaryContacts = (
        primaryContacts: PrimaryContactFormValues[],
      ) => {
        return primaryContacts.filter((contactInfo) =>
          Object.values(contactInfo).some(Boolean),
        );
      };

      const updatedValues: ClientInitialValues = {
        generalInfo: {
          ...generalInfoForm.values,
          primaryContacts: filterPrimaryContacts(
            generalInfoForm.values.primaryContacts,
          ),
        },
        billingInfo: billingInfoForm.values,
        additionalInfo: additionalInfoForm.values,
        serviceAgreement: {
          ...serviceAgreementForm.values,
          clientAgreement:
            serviceAgreementForm.values.serviceAgreement ===
            ServiceAgreementTypeEnum.ClientAgreement
              ? serviceAgreementForm.values.clientAgreement
              : [],
          memberAgreement:
            serviceAgreementForm.values.serviceAgreement ===
            ServiceAgreementTypeEnum.MemberAgreement
              ? serviceAgreementForm.values.memberAgreement
              : [],
          myBasePayAgreement:
            serviceAgreementForm.values.serviceAgreement ===
            ServiceAgreementTypeEnum.MyBasePayAgreement
              ? serviceAgreementForm.values.myBasePayAgreement
              : [],
        },
      };

      const initialCustomerValues = {
        ...initialValues,
        generalInfo: {
          ...initialValues?.generalInfo,
          primaryContacts: filterPrimaryContacts(
            initialValues?.generalInfo?.primaryContacts || [],
          ),
        },
      };

      if (isEqual(initialCustomerValues, updatedValues)) {
        BaseAlert.success('Customer has been updated.');
        return onClose();
      }
    }

    const {
      companyName,
      state,
      city,
      companyWebsite,
      additionalInvoiceDBA,
      mainAddress,
      zip,
      companyEmail,
      companyPhone,
      primaryContacts,
      country,
      internationalStateName,
      internationalCityName,
      customerGroupId,
      customerGroupName,
    } = generalInfoForm.values;

    const { billingContacts, billingLocation } = billingInfoForm.values;

    const {
      serviceAgreement,
      clientAgreement,
      memberAgreement,
      myBasePayAgreement,
      isAgreementNotAvailableConfirmed,
    } = serviceAgreementForm.values;

    const {
      clientSize,
      industry,
      haveBusinessBefore,
      yearsInBusiness,
      abilityToPay,
      billRate,
      duration,
      durationUnit,
      clientSuccessPartnerUserId,
      bdSalesContactUserId,
      billingSpecialistUserId,
    } = additionalInfoForm.values;

    const contactPerson = primaryContacts
      .map((contactPerson) => ({
        contactId: contactPerson.id || undefined,
        firstName: contactPerson.firstName || undefined,
        lastName: contactPerson.lastName || undefined,
        email: contactPerson.email || undefined,
        title: contactPerson.title || undefined,
        phoneNumber: contactPerson.phone || undefined,
      }))
      .filter(({ contactId, ...contactInfo }) =>
        Object.values(contactInfo).some(Boolean),
      );

    let agreementPath: FileValue[] = [];

    switch (serviceAgreement) {
      case ServiceAgreementTypeEnum.ClientAgreement:
        agreementPath = clientAgreement as FileValue[];
        break;
      case ServiceAgreementTypeEnum.MemberAgreement:
        agreementPath = memberAgreement as FileValue[];
        break;
      case ServiceAgreementTypeEnum.MyBasePayAgreement:
        agreementPath = myBasePayAgreement as FileValue[];
        break;
    }

    const memberAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.MemberAgreement &&
      initialValues?.serviceAgreement.memberAgreement?.length
        ? initialValues?.serviceAgreement.memberAgreement
        : undefined;
    const clientAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.ClientAgreement &&
      initialValues?.serviceAgreement.clientAgreement?.length
        ? initialValues?.serviceAgreement.clientAgreement
        : undefined;
    const myBasePayAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.MyBasePayAgreement &&
      initialValues?.serviceAgreement.myBasePayAgreement?.length
        ? initialValues?.serviceAgreement.myBasePayAgreement
        : undefined;

    const initialFiles =
      memberAgreementFiles || clientAgreementFiles || myBasePayAgreementFiles;

    const data: CreateCustomerRequest = {
      generalInfo: {
        companyInfo: {
          companyName: companyName.trim(),
          companyWebSite: companyWebsite.trim(),
          additionalInvoiceName: additionalInvoiceDBA
            ? additionalInvoiceDBA.trim()
            : undefined,
          companyEmail: companyEmail.trim() || undefined,
          companyPhoneNumber: companyPhone,
          location: {
            countryId: toNumber(country),
            stateId: toNumber(state) || undefined,
            internationalStateName: internationalStateName || undefined,
            internationalCityName: internationalCityName || undefined,
            city: city || undefined,
            address: mainAddress,
            zipCode: zip || undefined,
          },
        },
        primaryContacts: contactPerson,
      },
      billingInfo: {
        billingContacts: billingContacts,
        location: {
          countryId: billingLocation.locationSameAsMain
            ? toNumber(country)
            : billingLocation.country || undefined,
          stateId: billingLocation.locationSameAsMain
            ? toNumber(state) || undefined
            : billingLocation.state || undefined,
          internationalStateName: billingLocation.locationSameAsMain
            ? internationalStateName
            : billingLocation.internationalStateName || undefined,
          city: billingLocation.locationSameAsMain
            ? city
            : billingLocation.city || undefined,
          internationalCityName: billingLocation.locationSameAsMain
            ? internationalCityName
            : billingLocation.internationalCityName || undefined,
          address: billingLocation.locationSameAsMain
            ? mainAddress
            : billingLocation.address || undefined,
          zipCode: billingLocation.locationSameAsMain
            ? zip || undefined
            : billingLocation.zip || undefined,
        },
        isBillingSameAsMainAddress:
          billingLocation.locationSameAsMain || undefined,
      },
      additionalInfo: {
        potentialHeadcount: clientSize,
        averageBillRate: !isNull(billRate) ? billRate : undefined,
        estimatedValue: duration || undefined,
        estimatedType: durationUnit || DurationTypeEnum.Week,
        industryName: industry ? industry.trim() : undefined,
        haveBusinessBefore,
        yearsInBusiness: yearsInBusiness,
        abilityToPay: abilityToPay,
        mbpContactsInfo: {
          clientSuccessPartnerUserId:
            clientSuccessPartnerUserId || undefined,
          bdSalesContactUserId: bdSalesContactUserId || undefined,
          billingSpecialistUserId: billingSpecialistUserId || undefined,
        },
      },
      agreementDetailsInfo: {
        agreementDetailsType: serviceAgreement,
        agreementAttachments: agreementPath?.length
          ? agreementPath.map((file) => ({
              fileKey: file.s3Key,
              fileName: file.fileName,
              createdDate:
                initialFiles?.find(
                  (initialFile) => initialFile.s3Key === file.s3Key,
                )?.createdDate || undefined,
            }))
          : undefined,
        isAgreementNotAvailableConfirmed: isAgreementNotAvailableConfirmed,
      },
      customerGroupId: customerGroupId || undefined,
      customerGroupName: customerGroupName || undefined,
    };

    let requestData: any;
    let correctAction: AsyncThunk<CreateCustomerResponse, any, {}>;
    let successText: string;

    if (creationType === ClientCreationEnum.EditImport && customerId) {
      onSetClientValues && onSetClientValues(data, customerId);
      return onClose();
    }

    switch (creationType) {
      case ClientCreationEnum.EditDraft:
        correctAction = crateClientFromDraft;
        requestData = {
          customerId,
          data,
        };
        successText = 'New customer request has been submitted.';
        break;
      case ClientCreationEnum.Edit:
        correctAction = updateClient;
        requestData = {
          isAdmin: isAdminRole,
          customerId,
          data,
        };
        successText = 'Customer has been updated.';
        break;

      default:
        // ClientCreationEnum.Create is a default
        correctAction = createNewClient;
        requestData = data;
        successText = 'New customer request has been submitted.';
        break;
    }

    setIsLoading(true);
    const result = await dispatch(correctAction(requestData));
    setIsLoading(false);

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success(successText);

      // Replace customerId with customerKey after migrating from draft to request
      // https://mybasepay.atlassian.net/browse/MBO-2394
      if (creationType === ClientCreationEnum.EditDraft) {
        navigate(`/customer/${result.payload.customerKey}`, { replace: true });
      } else {
        onClose();
        onSubmit?.();
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      resetFormSubmitting();
    }
  };

  const handleDraftSave = async () => {
    const generalInfoForm: GeneralInfoFormikProps = generalInfoFormRef.current;
    const billingInfoForm: BillingInfoFormikProps = billingInfoFormRef.current;
    const additionalInfoForm: AdditionalInfoFormikProps =
      additionalInfoFormRef.current;
    const serviceAgreementForm: ServiceAgreementFormikProps =
      serviceAgreementFormRef.current;

    if (isFormSubmitting()) return;

    const {
      companyName,
      country,
      internationalStateName,
      internationalCityName,
      state,
      city,
      companyWebsite,
      additionalInvoiceDBA,
      mainAddress,
      zip,
      primaryContacts,
      companyEmail,
      companyPhone,
      customerGroupId,
      customerGroupName,
    } = generalInfoForm.values;

    const { billingContacts, billingLocation } = billingInfoForm.values;

    const {
      serviceAgreement,
      clientAgreement,
      memberAgreement,
      myBasePayAgreement,
      isAgreementNotAvailableConfirmed,
    } = serviceAgreementForm.values;

    const {
      clientSize,
      industry,
      haveBusinessBefore,
      yearsInBusiness,
      abilityToPay,
      billRate,
      duration,
      durationUnit,
      clientSuccessPartnerUserId,
      bdSalesContactUserId,
      billingSpecialistUserId,
    } = additionalInfoForm.values;

    const contactPerson = primaryContacts
      .map((contactPerson) => ({
        contactId: contactPerson.id || undefined,
        firstName: contactPerson.firstName || undefined,
        lastName: contactPerson.lastName || undefined,
        email: contactPerson.email || undefined,
        title: contactPerson.title || undefined,
        phoneNumber: contactPerson.phone || undefined,
      }))
      .filter((contactPerson) => Object.values(contactPerson).some(Boolean));

    let agreementPath: FileValue[] = [];

    switch (serviceAgreement) {
      case ServiceAgreementTypeEnum.ClientAgreement:
        agreementPath = clientAgreement as FileValue[];
        break;
      case ServiceAgreementTypeEnum.MemberAgreement:
        agreementPath = memberAgreement as FileValue[];
        break;
      case ServiceAgreementTypeEnum.MyBasePayAgreement:
        agreementPath = myBasePayAgreement as FileValue[];
        break;
    }

    const memberAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.MemberAgreement &&
      initialValues?.serviceAgreement.memberAgreement?.length
        ? initialValues?.serviceAgreement.memberAgreement
        : undefined;
    const clientAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.ClientAgreement &&
      initialValues?.serviceAgreement.clientAgreement?.length
        ? initialValues?.serviceAgreement.clientAgreement
        : undefined;
    const myBasePayAgreementFiles =
      serviceAgreement === ServiceAgreementTypeEnum.MyBasePayAgreement &&
      initialValues?.serviceAgreement.myBasePayAgreement?.length
        ? initialValues?.serviceAgreement.myBasePayAgreement
        : undefined;

    const initialFiles =
      memberAgreementFiles || clientAgreementFiles || myBasePayAgreementFiles;

    const data: CreateCustomerRequest = {
      generalInfo: {
        companyInfo: {
          companyName: companyName.trim(),
          companyWebSite: companyWebsite ? companyWebsite.trim() : undefined,
          additionalInvoiceName: additionalInvoiceDBA
            ? additionalInvoiceDBA.trim()
            : undefined,
          companyEmail: companyEmail.trim() || undefined,
          companyPhoneNumber: companyPhone || undefined,
          location: {
            countryId: !isNil(country) ? country : undefined,
            stateId: toNumber(state) || undefined,
            internationalStateName: internationalStateName || undefined,
            city: city || undefined,
            internationalCityName: internationalCityName || undefined,
            address: mainAddress || undefined,
            zipCode: zip || undefined,
          },
        },
        primaryContacts: contactPerson,
      },
      billingInfo: {
        billingContacts: billingContacts,
        location: {
          countryId: billingLocation.locationSameAsMain
            ? country
            : billingLocation.country,
          stateId: billingLocation.locationSameAsMain
            ? toNumber(state)
            : billingLocation.state || undefined,
          internationalStateName: billingLocation.locationSameAsMain
            ? internationalStateName
            : billingLocation.internationalStateName || undefined,
          internationalCityName: billingLocation.locationSameAsMain
            ? internationalCityName
            : billingLocation.internationalCityName || undefined,
          city: billingLocation.locationSameAsMain
            ? city
            : billingLocation.city || undefined,
          address: billingLocation.locationSameAsMain
            ? mainAddress
            : billingLocation.address || undefined,
          zipCode: billingLocation.locationSameAsMain
            ? zip || undefined
            : billingLocation.zip || undefined,
        },
        isBillingSameAsMainAddress:
          billingLocation.locationSameAsMain || undefined,
      },
      additionalInfo: {
        potentialHeadcount: clientSize,
        averageBillRate: !isNull(billRate) ? billRate : undefined,
        estimatedValue: duration || undefined,
        estimatedType: durationUnit || DurationTypeEnum.Week,
        industryName: industry ? industry.trim() : undefined,
        haveBusinessBefore,
        yearsInBusiness: yearsInBusiness,
        abilityToPay: abilityToPay,
        mbpContactsInfo: {
          clientSuccessPartnerUserId:
            clientSuccessPartnerUserId || undefined,
          bdSalesContactUserId: bdSalesContactUserId || undefined,
          billingSpecialistUserId: billingSpecialistUserId || undefined,
        },
      },
      agreementDetailsInfo: {
        agreementDetailsType: serviceAgreement,
        agreementAttachments: agreementPath?.length
          ? agreementPath.map((file) => ({
              fileKey: file.s3Key,
              fileName: file.fileName,
              createdDate:
                initialFiles?.find(
                  (initialFile) => initialFile.s3Key === file.s3Key,
                )?.createdDate || undefined,
            }))
          : undefined,
        isAgreementNotAvailableConfirmed: isAgreementNotAvailableConfirmed,
      },
      customerGroupId: customerGroupId || undefined,
      customerGroupName: customerGroupName || undefined,
    };

    let requestData: any;
    let correctAction: any;
    let successText: string;

    switch (creationType) {
      case ClientCreationEnum.EditDraft:
        correctAction = updateDraftClient;
        requestData = {
          customerId,
          data,
        };
        successText = 'Draft of customer creation request updated.';
        break;

      default:
        // ClientCreationEnum.Create is a default
        correctAction = createDraftClient;
        requestData = data;
        successText = 'Customer draft created and saved in Drafts.';
        break;
    }

    setIsDraftSaving(true);
    const result = await dispatch(correctAction(requestData));
    setIsDraftSaving(false);

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success(successText);
      onClose();

      onSubmit?.();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
      resetFormSubmitting();
    }
  };

  return (
    <BaseModal
      title={getModalTitle(creationType)}
      maxWidth={888}
      destroyOnClose
      open={true}
      onCancel={handleModalCancel}
      className="modal-with-tabs"
    >
      <Tabs
        activeKey={toString(activeTab)}
        onChange={(activeKey: string) => handleTabChange(activeKey)}
        animated={false}
        className="icon-tabs modal-scroll-wrapper"
      >
        <Tabs.TabPane
          key={toString(TabKeys.GeneralInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="newCustomerGeneralInfoTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.GeneralInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.GeneralInfo].status)}
            </div>
          }
        >
          <GeneralInfo
            formRef={generalInfoFormRef}
            onChange={onFormChange[TabKeys.GeneralInfo]}
            initialValues={initialValues?.generalInfo}
            creationType={creationType}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.BillingInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="newCustomerBillingInfoTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.BillingInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.BillingInfo].status)}
            </div>
          }
        >
          <BillingInfo
            formRef={billingInfoFormRef}
            onChange={onFormChange[TabKeys.BillingInfo]}
            mainLocation={billingLocation}
            initialValues={initialValues?.billingInfo}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.AdditionalInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="newCustomerAdditionalInfoTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.AdditionalInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.AdditionalInfo].status)}
            </div>
          }
        >
          <AdditionalInfo
            formRef={additionalInfoFormRef}
            onChange={onFormChange[TabKeys.AdditionalInfo]}
            initialValues={initialValues?.additionalInfo}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.ServiceAgreement)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="newCustomerServiceAgreementTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.ServiceAgreement].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.ServiceAgreement].status)}
            </div>
          }
        >
          <ServiceAgreement
            formRef={serviceAgreementFormRef}
            onChange={onFormChange[TabKeys.ServiceAgreement]}
            initialValues={initialValues?.serviceAgreement}
            isAdmin={isAdminRole}
          />
        </Tabs.TabPane>
      </Tabs>

      <BaseModalFooter className="button-space-between">
        <div className="d-flex">
          {isDraftAvailable && (
            <Button
              onClick={handleDraftSave}
              disabled={!isDraftValid || isDraftSaving}
              loading={isDraftSaving}
              type="primary"
            >
              Save as Draft
            </Button>
          )}

          <Button
            className="with-icon modal-icon-btn"
            onClick={handleModalCancel}
          >
            <span className="icon icon-closed-outlined" />
            Cancel
          </Button>
        </div>

        <div className="d-flex">
          {activeTab !== TabKeys.GeneralInfo && (
            <Button
              className="with-icon modal-icon-btn"
              onClick={handlePrevStep}
            >
              <span className="icon icon-double-left" />
              Back
            </Button>
          )}

          {activeTab < tabs.length - 1 && (
            <Button
              className="with-icon"
              type="primary"
              onClick={handleNextStep}
            >
              Next
              <span className="icon icon-double-right" />
            </Button>
          )}

          {activeTab === tabs.length - 1 && (
            <Button
              type="primary"
              data-id="createNewClientBtn"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleSubmit}
            >
              {getModalSubmitName(creationType)}
            </Button>
          )}
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}
